import React, { FC, useEffect, useRef, useState } from 'react';
import { useAuth0 } from 'Auth';
import { useHistory } from 'react-router-dom';
import {
  useLazyIncentiveProgramsApi,
  useNewIncentiveProgramsApi,
} from 'Shared/services/Api';
import { useRewardsEnrollmentContext } from 'Shared/providers/RewardsEnrollmentProvider';
import { useAppContext } from 'QuoteApp/services/providers';
import { GetAgentStatusResponse } from 'Rewards/services/endpoints/GetAgentStatus/types';
import { MOCK_AGENT_STATUS } from 'Rewards/services/endpoints/GetAgentStatus/mockStatus';
import {
  GetRewardsConfigResponse,
  entryThresholdFromConfig,
  maxWinPrizeForPhase,
} from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { MOCK_REWARDS_CONFIG } from 'Rewards/services/endpoints/GetRewardsConfig/mockConfig';
import { reportEvent } from 'Shared/analytics';
import { env } from 'Shared/constants/env';
import { Brand, InfoIcon } from '@clearcover/atlas';
import styles from './styles.module.css';

const shouldDisplay = (
  enrollment: ReturnType<
    typeof useRewardsEnrollmentContext
  >['incentiveProgramEnrollmentResponse'],
  details: ReturnType<typeof useAppContext>['details']
) => {
  if (!enrollment) return false;
  const isPendingTerms = enrollment.status === 'TC_PENDING';
  if (!enrollment.isEnrolled && !isPendingTerms) return false;
  return details?.address?.[details?.principalInsuredId]?.state?.value === 'FL';
};

export const RewardsTab: FC = () => {
  const {
    incentiveProgramEnrollmentResponse,
    refresh: refreshIncentiveProgramEnrollment,
  } = useRewardsEnrollmentContext();
  const { details } = useAppContext();
  const { user } = useAuth0();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const hasReportedOpen = useRef(false);

  const [{ data: statusData, loading }, executeStatus] =
    useLazyIncentiveProgramsApi<GetAgentStatusResponse>({});

  const [{ loading: acceptLoading, error: acceptError }, executeAcceptTerms] =
    useLazyIncentiveProgramsApi<{ status: string }>({
      method: 'POST',
      url: `/agents/${user?.id}/accept-terms`,
    });

  // Config is program-level and small; fetch eagerly so the panel has prize +
  // threshold the moment it opens. Skipped when mocks are enabled.
  const [{ data: configData, loading: configLoading }] =
    useNewIncentiveProgramsApi<GetRewardsConfigResponse>(
      { url: '/config/current' },
      false,
      false,
      env.rewardsMockEnabled
    );

  const status = env.rewardsMockEnabled ? MOCK_AGENT_STATUS : statusData;
  const config = env.rewardsMockEnabled ? MOCK_REWARDS_CONFIG : configData;

  useEffect(() => {
    if (isOpen && status) {
      if (!hasReportedOpen.current) {
        hasReportedOpen.current = true;
        reportEvent('26for26-rewards-tab:open');
      }
    } else if (!isOpen) {
      hasReportedOpen.current = false;
    }
  }, [isOpen, status]);

  if (!shouldDisplay(incentiveProgramEnrollmentResponse, details)) return null;

  const isPendingTerms =
    incentiveProgramEnrollmentResponse?.status === 'TC_PENDING';

  const handleTabClick = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening && !env.rewardsMockEnabled && user?.id && !statusData) {
      executeStatus({ url: `/agents/${user.id}/status` });
    }
  };

  const handleClose = () => setIsOpen(false);

  const handleCheckoutClick = () => {
    reportEvent('26for26-rewards-tab:checkout-click');
    setIsOpen(false);
    history.push('/rewards');
  };

  const handleAcceptTerms = () => {
    reportEvent('26for26-rewards-tab:tc-agree-click');
    setRefreshError(false);
    executeAcceptTerms({
      data: { programCode: '26FOR26', termsVersion: 'v1.0' },
    })
      .then(() =>
        refreshIncentiveProgramEnrollment().catch(() => setRefreshError(true))
      )
      .catch(() => {});
  };

  const availableEntries = status?.availableEntries ?? 0;
  const policiesRemaining = status?.progress?.policiesUntilNextEntry ?? 0;
  const currentPhaseCode = status?.phase?.code ?? config?.phase?.code;
  const entryThreshold = config ? entryThresholdFromConfig(config) : null;
  const maxPrize =
    config && currentPhaseCode
      ? maxWinPrizeForPhase(config, currentPhaseCode)
      : null;
  const filledSegments =
    entryThreshold != null
      ? Math.max(0, entryThreshold - policiesRemaining)
      : 0;

  return (
    <div
      className={`${styles.tabContainer}${
        isOpen ? ` ${styles.tabContainerOpen}` : ''
      }`}
    >
      <button
        className={styles.tabButton}
        onClick={handleTabClick}
        data-testid="rewards-tab-trigger"
        aria-label="26 for 26 Rewards"
        aria-expanded={isOpen}
      >
        <Brand variant="monogram" color="lime" width={24} height={24} />
        <div className={styles.tabLabelWrapper}>
          <span className={styles.tabLabel}>REWARDS</span>
        </div>
      </button>

      {isOpen && (
        <div className={styles.card} data-testid="rewards-tab-card">
          <div className={styles.cardHeader}>
            <div className={styles.headerLeft}>
              <span className={styles.clearcover}>CLEARCOVER</span>
              <span className={styles.programName}>26 FOR '26</span>
            </div>
            <div className={styles.headerRight}>
              {(user?.firstName || user?.lastName) && (
                <div className={styles.agentInfo}>
                  <span className={styles.agentName}>
                    {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                  </span>
                </div>
              )}
              <button
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close rewards panel"
                data-testid="rewards-tab-close"
              >
                ×
              </button>
            </div>
          </div>

          <div className={styles.cardBody}>
            {isPendingTerms ? (
              <div
                className={styles.tcPrompt}
                data-testid="rewards-tab-tc-prompt"
              >
                <h2
                  className={styles.tcTitle}
                  data-testid="rewards-tab-tc-title"
                >
                  Welcome to 26 for &apos;26!
                </h2>
                <p className={styles.tcBody}>
                  We&apos;re offering rewards to thank our partners for choosing
                  Clearcover. Every point you earn is worth $1 to spend on
                  things you love.
                </p>
                <p className={styles.tcBodyBold}>
                  Play a game, earn rewards, and compete for grand prizes.
                </p>
                <div className={styles.tcDivider} />
                <p className={styles.tcInstruction}>
                  To get started with 26 for &apos;26, please read and accept
                  our Terms and Conditions
                </p>
                <a
                  href="https://clearcover.com/26-for-26-terms"
                  target="_blank"
                  rel="noreferrer"
                  className={styles.tcLink}
                  data-testid="rewards-tab-tc-link"
                  onClick={() =>
                    reportEvent('26for26-rewards-tab:tc-link-click')
                  }
                >
                  Click here to view Terms &amp; Conditions
                </a>
                <p className={styles.tcAgreeInstruction}>
                  Once you have read and agreed to accept the terms, please
                  click <strong>I Agree</strong> to continue to 26 for &apos;26!
                </p>
                <button
                  className={styles.tcAgreeButton}
                  onClick={handleAcceptTerms}
                  disabled={acceptLoading}
                  data-testid="rewards-tab-tc-agree-button"
                >
                  {acceptLoading ? 'Processing...' : 'I Agree'}
                </button>
                {(acceptError || refreshError) && (
                  <p
                    className={styles.tcError}
                    data-testid="rewards-tab-tc-error"
                  >
                    Something went wrong. Please try again.
                  </p>
                )}
              </div>
            ) : (
              <div className={styles.cardInner}>
                {(loading || configLoading) && !env.rewardsMockEnabled ? (
                  <p className={styles.loadingText}>Loading...</p>
                ) : (
                  <>
                    <p className={styles.spinToWinLabel}>SPIN-TO-WIN</p>
                    <div className={styles.prizeRow}>
                      <span className={styles.prizeAmount}>
                        {maxPrize != null
                          ? `Win up to $${maxPrize}`
                          : 'SPIN-TO-WIN'}
                      </span>
                      <span className={styles.perSpin}>/spin</span>
                    </div>

                    <div
                      className={styles.entriesRow}
                      data-testid="rewards-tab-entries"
                    >
                      <span className={styles.entriesCount}>
                        {availableEntries}
                      </span>
                      <span className={styles.entriesLabel}>
                        {availableEntries === 1 ? 'ENTRY' : 'ENTRIES'} AVAILABLE
                        <br />
                        TO PLAY
                      </span>
                    </div>

                    {status?.progress && entryThreshold != null && (
                      <div className={styles.progressSection}>
                        <div className={styles.progressLabels}>
                          <span>Next entry</span>
                          <span>
                            {policiesRemaining}{' '}
                            {policiesRemaining === 1 ? 'SALE' : 'SALES'} AWAY
                          </span>
                        </div>
                        <div className={styles.progressBar}>
                          {Array.from({ length: entryThreshold }).map(
                            (_, i) => (
                              <div
                                key={i}
                                className={
                                  i < filledSegments
                                    ? styles.progressSegmentFilled
                                    : styles.progressSegmentEmpty
                                }
                              />
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {!isPendingTerms && (
            <div className={styles.cardFooter}>
              <button
                className={styles.checkoutNote}
                onClick={handleCheckoutClick}
                data-testid="rewards-tab-checkout-note"
              >
                <InfoIcon className={styles.infoIcon} />
                <span>Checkout the rewards section to play</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
